import React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Plus, Server as ServerIcon, Trash2 } from 'lucide-react';
import {
  useCreateServer,
  useGetPaginatedServers,
  useDeleteServer,
  useTestServerConnection,
} from '../../../helpers/api/servers/server';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import { Button } from '../../ComponnentsUI/common/Button';
import { ConfirmDialog } from '../../ComponnentsUI/common/ConfirmDialog';
import Table from '../Table/Table';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  PaginationState,
} from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import ServerCreationModal from './server-creation-modal';
import EchoTestModal from './echo-test-modal';

const Servers = () => {
  const { t } = useTranslation(['Organization']);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [isEchoModalVisible, setIsEchoModalVisible] = React.useState(false);
  const [selectedServer, setSelectedServer] = React.useState<any>(null);
  const [echoTestResult, setEchoTestResult] = React.useState<any>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<any>(null);
  const queryClient = useQueryClient();
  const {
    data: servers,
    isLoading,
    error,
  } = useGetPaginatedServers(pagination.pageIndex + 1, pagination.pageSize);

  function onSuccessCreateServer(response: AxiosResponse) {
    messageI18n.success('devices.serverCreated');
    queryClient.invalidateQueries({ queryKey: ['paginated-servers'] });
    setIsModalVisible(false);
  }

  function onErrorCreateServer(error: any) {
    console.error('Error creating server:', error);
    message.error(error?.response?.data?.message || tToast('common.genericError'));
  }

  const { mutate: createServer, isPending: isCreatingServer } = useCreateServer(
    onSuccessCreateServer,
    onErrorCreateServer
  );
  function onSuccessDeleteServer(response: AxiosResponse) {
    messageI18n.success('devices.serverDeleted');
    queryClient.invalidateQueries({ queryKey: ['paginated-servers'] });
  }

  function onErrorDeleteServer(error: any) {
    console.error('Error deleting server:', error);
    message.error(error?.response?.data?.message || tToast('common.genericError'));
  }

  const { mutate: deleteServer } = useDeleteServer(
    onSuccessDeleteServer,
    onErrorDeleteServer
  );

  function onSuccessTestConnection(response: AxiosResponse) {
    const result = response.data;
    setEchoTestResult(result);
    if (result?.success) {
      messageI18n.success('devices.connectionTestSuccess');
    } else {
      messageI18n.error('devices.connectionTestFailed');
    }
  }

  function onErrorTestConnection(error: any) {
    console.error('Error testing connection:', error);
    const errorMessage = error?.response?.data?.message || tToast('devices.connectionTestFailed');
    setEchoTestResult({
      success: false,
      message: errorMessage,
    });
    message.error(errorMessage);
  }

  const { mutate: testConnection, isPending: isTestingConnection } =
    useTestServerConnection(onSuccessTestConnection, onErrorTestConnection);

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleCreateServer = (values: any) => {
    createServer(values);
  };

  const handleOpenEchoModal = (server: any) => {
    setSelectedServer(server);
    setEchoTestResult(null);
    setIsEchoModalVisible(true);
  };

  const handleCloseEchoModal = () => {
    setIsEchoModalVisible(false);
    setSelectedServer(null);
    setEchoTestResult(null);
  };

  const handleTestConnection = () => {
    if (selectedServer) {
      testConnection({ server_id: selectedServer.id });
    }
  };

  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'server_ae_title',
        header: () => <span>{t('servers.columnAeTitle')}</span>,
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'server_host',
        header: () => <span>{t('servers.columnHost')}</span>,
        footer: (props) => props.column.id,
      },
      {
        accessorKey: 'server_port',
        header: () => <span>{t('servers.columnPort')}</span>,
        footer: (props) => props.column.id,
      },

      {
        accessorKey: 'created_at',
        header: () => <span>{t('servers.columnCreated')}</span>,
        footer: (props) => props.column.id,
        cell: ({ row }) => {
          const date = new Date(row.original.created_at);
          return date.toISOString().split('T')[0];
        },
      },
      {
        header: t('servers.actions'),
        cell: (cellProps) => {
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => handleOpenEchoModal(cellProps.row.original)}
                title="Test connection (C-ECHO)"
                className="rounded-md p-1.5 text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                <Activity size={16} />
              </button>
              <button
                type="button"
                onClick={() => setDeleteTarget(cellProps.row.original)}
                title="Delete server"
                className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <Trash2 size={16} />
              </button>
            </div>
          );
        },
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: servers?.data ?? [],
    columns,
    rowCount: servers?.data?.total_count,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    debugTable: true,
  });

  const totalCount =
    (servers as any)?.total_count ??
    (servers as any)?.data?.total_count ??
    servers?.data?.length ??
    0;

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white py-12 text-center dark:border-gray-700 dark:bg-gray-800">
        <ServerIcon size={28} className="text-slate-400 dark:text-gray-500" />
        <p className="text-sm text-slate-500 dark:text-gray-400">
          {t('servers.error', { message: error.message })}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        <div className="flex justify-between items-center w-full mb-4">
          <div className="flex items-center gap-2">
            <ServerIcon size={18} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-title-page-color-100 dark:text-white">
              {t('servers.title')}
            </h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-400">
              {totalCount}
            </span>
          </div>
          <Button variant="primary" size="sm" onClick={handleOpenModal}>
            <Plus size={15} className="mr-1.5" />
            {t('servers.create')}
          </Button>
        </div>

        {!isLoading && totalCount === 0 ? (
          <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center dark:border-gray-600 dark:bg-gray-800">
            <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-gray-700 dark:text-gray-500">
              <ServerIcon size={22} />
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {t('servers.emptyTitle', 'No DICOM servers configured yet')}
            </p>
            <p className="max-w-sm text-xs text-slate-400 dark:text-slate-500">
              {t(
                'servers.emptyHint',
                'Register a remote PACS or archive so studies can be sent to it from this instance.'
              )}
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleOpenModal}>
              <Plus size={14} className="mr-1.5" />
              {t('servers.create')}
            </Button>
          </div>
        ) : (
          <Table
            table={table}
            totalCount={totalCount}
            isLoading={isLoading}
            pageSize={pagination.pageSize}
            columnsLength={columns.length}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title={t('servers.deleteConfirmTitle', {
          defaultValue: 'Delete server "{{name}}"?',
          name: deleteTarget?.server_ae_title ?? '',
        })}
        message={
          deleteTarget
            ? t('servers.deleteConfirmMessage', {
                defaultValue:
                  'The DICOM server at {{host}}:{{port}} will be removed. Studies can no longer be sent to it.',
                host: deleteTarget.server_host,
                port: deleteTarget.server_port,
              })
            : ''
        }
        confirmLabel={t('servers.deleteConfirm', 'Delete')}
        onConfirm={() => {
          if (deleteTarget) deleteServer({ id: deleteTarget.id });
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ServerCreationModal
        visible={isModalVisible}
        onCancel={handleCloseModal}
        onSubmit={handleCreateServer}
        loading={isCreatingServer}
      />

      <EchoTestModal
        visible={isEchoModalVisible}
        onClose={handleCloseEchoModal}
        serverInfo={selectedServer}
        testResult={echoTestResult}
        loading={isTestingConnection}
        onTest={handleTestConnection}
      />
    </>
  );
};

export default Servers;
